<script lang="ts">
	//Hooks
	import { get } from 'svelte/store';
	// Services
	import { format } from 'svelte-i18n';
	// Stores
	import { cloud_disabled_devices } from 'src/stores/device-runtime/firmware-ui';
	// Design system components
	import { Loader, Text, Tabs, Tab } from '@Shelly/shelly-design-system';
	// Helpers
	import { debounce } from '../../helpers/utilities';
	import { updateSingleChannelInterfaceData } from '../../helpers/add_delete_helpers';
	import { getEnabledHourControl } from './chart_control_helpers';
	import { getDiviceTimeZoneDate } from './chart_helper';
	import { getStatistics } from './chart_get_stats_period';
	import { isExperimentalNetMeteringEnabled } from './chart_net_metering_helpers';
	import { devices } from 'src/stores/lists/devices';
	import { isThreeEM, isWeatherStation, real_id } from 'src/helpers/interface_helpers';
	import { getLast24WSStatistics } from '../WeatherStation/weather_station_statistics_helper';
	// Components
	import ChartControls from './ChartControls.svelte';
	import ChartStatistic from './ChartStatistic.svelte';
	import DeviceChart from './DeviceChart.svelte';
	// Types
	import type { chartControlsDataInterface } from './chart_control_helpers';
	import type { ChartTimeType } from './chart_more_actions_helper';
	import type { Device } from '../../types/Device';
	import type { ChartConfig, ChartStatisticInterface, ChartDataInterface } from '../../types/Chart';

	export let chartConfig: ChartConfig, reqURL: string, device: Device;
	export let fullScreenDeviceChartObj = null;
	export let statsData: Promise<{
		chartData: ChartDataInterface;
		chartStatistic: ChartStatisticInterface;
	}>;
	export let chartElement: DeviceChart;
	export let chartTime: ChartTimeType;
	export let dummyNetMetering = false;

	let chartControlsElement: ChartControls;

	let showTariffs = device?.[`chartSettings`]?.showTariffs;
	let selectedChartControlsData: chartControlsDataInterface = null;

	const deviceCategory = device?.category;
	const enabledHourControl = getEnabledHourControl();
	const timeZone = getDiviceTimeZoneDate(device);
	const measureNetMetering = $devices?.[real_id(device?.id)]?.cloud_options?.measure_net_energy;
	let netMetering = measureNetMetering > 0;

	const getStatsPeriod = async (
			chartControlsData: chartControlsDataInterface,
			netMeteringPeriod: number = 0,
			dummyNetMetering: boolean,
		): Promise<{
			chartData: ChartDataInterface;
			chartStatistic: ChartStatisticInterface;
		}> => {
			selectedChartControlsData = chartControlsData;
			if (
				chartControlsData.currentPeriod === '24_hours' &&
				chartConfig.id === 'precipitation' &&
				isWeatherStation(device.type)
			) {
				return getLast24WSStatistics(device, chartConfig.id);
			}
			return getStatistics(
				device,
				chartControlsData,
				chartConfig,
				reqURL,
				netMeteringPeriod,
				true,
				dummyNetMetering,
			);
		},
		chartControlsEvent = (e: CustomEvent) => {
			chartTime = e.detail.time;
			statsData = getStatsPeriod(e.detail, netMetering ? measureNetMetering : 0, dummyNetMetering);
		},
		onChangeEvent = (e: CustomEvent) => {
			if (e.detail.action == 'toggleLegend') {
				chartConfig.series[e.detail.index].visible = e.detail.visible;
			} else if (e.detail.action == 'dblClick') {
				chartControlsElement.changeDateRangeOnClick(e);
			}
		},
		onShowTariffs = () => {
			if (!device?.shared) {
				device[`chartSettings`].showTariffs = showTariffs;
				debounce(async () => {
					await updateSingleChannelInterfaceData(device);
				}, 500);
			}
		};
	const statisticsTabOnChange = (statis: boolean) => {
		netMetering = statis;
		statsData = getStatsPeriod(
			selectedChartControlsData,
			netMetering ? measureNetMetering : 0,
			dummyNetMetering,
		);
	};

	$: if (selectedChartControlsData && dummyNetMetering !== undefined) {
		statsData = getStatsPeriod(
			selectedChartControlsData,
			netMetering ? measureNetMetering : 0,
			dummyNetMetering,
		);
	}
</script>

{#if get(cloud_disabled_devices)?.[device.id]}
	<Text class="mt-m mb-s body-small-styles text-center text-warning"
		>{$format('charts_cloud_disabled_msg', {
			default: 'You need to have "cloud" enabled for the device to collect consumption data.',
		})}</Text
	>
{:else}
	<ChartControls
		bind:this={chartControlsElement}
		chartControlID={`chart-range-dp-${chartConfig.name}-${chartConfig.extid}`}
		{enabledHourControl}
		{timeZone}
		enabledBillingDate={/(consumption|counter_accumulation)/.test(chartConfig.name) ||
			chartConfig.deviceCategory === 'charger'}
		on:init={chartControlsEvent}
		on:change={chartControlsEvent}
	/>
	{#if measureNetMetering > 0 && chartConfig.name == 'consumption' && isExperimentalNetMeteringEnabled()}
		<Tabs class="mt-s mb-s">
			<Tab
				active={!netMetering}
				on:click={() => {
					statisticsTabOnChange(false);
				}}
			>
				{$format('statistics_tab', {
					default: 'Statistics',
				})}
			</Tab>
			<Tab
				active={netMetering}
				on:click={() => {
					statisticsTabOnChange(true);
				}}
			>
				{$format('net_metering_tab', {
					default: 'Net Metering',
				})}
			</Tab>
		</Tabs>
	{/if}

	{#if statsData}
		{#await statsData}
			<div
				class="d-flex justify-content-center align-items-center"
				style={`height: ${chartConfig.height}px`}
			>
				<Loader size="m" />
			</div>
		{:then result}
			<DeviceChart
				bind:this={chartElement}
				chartData={result.chartData}
				{deviceCategory}
				{timeZone}
				on:change={onChangeEvent}
			/>
			<ChartStatistic
				chartData={result.chartData}
				chartStatistic={result.chartStatistic}
				bind:showTariffs
				on:change={onShowTariffs}
				{fullScreenDeviceChartObj}
			/>
			{#if result?.chartStatistic?.threeEMStatistic}
				{#each result.chartStatistic.threeEMStatistic as phase}
					<ChartStatistic chartData={result.chartData} chartStatistic={phase} />
				{/each}
			{/if}
		{:catch error}
			<div
				class="d-flex justify-content-center align-items-center"
				style={`height: ${chartConfig.height}px`}
			>
				<Text class="body-small-styles text-center mt-m mb-s ">
					<i class="fas fa-exclamation-triangle text-warning mr-xs" />
					{error.message}
				</Text>
			</div>
		{/await}
	{/if}
{/if}
